import React, { useCallback, useMemo, useState } from 'react';
import type {
  DataTableColumn,
  DataTableRenderCellParams,
  DataTableRowEventHandler
} from '@/components/custom/erp/data-table-v2';
import { CellFieldV2, ErpInputTableV2 } from '@/components/custom/erp';

interface PrintColumnRow {
  id: number;
  columnName: string;
  englishName: string;
  width: number;
  format: string;
  alignment: string;
  bold: boolean;
  italic: boolean;
  total: boolean;
}

const formatOptions = ['Text', 'Number', 'Currency', 'Date', 'Percentage'].map(value => ({ value, label: value }));
const alignmentOptions = ['Left', 'Center', 'Right'].map(value => ({ value, label: value }));

export const EditPrintTemplatePrintColumns: React.FC = () => {
  const [selectedRowId, setSelectedRowId] = useState<string>();
  const [tableData, setTableData] = useState<PrintColumnRow[]>([
    {
      id: 1,
      columnName: 'Mã khách hàng',
      englishName: 'Customer Code',
      width: 120,
      format: 'Text',
      alignment: 'Left',
      bold: false,
      italic: false,
      total: false
    },
    {
      id: 2,
      columnName: 'Tên khách hàng',
      englishName: 'Customer Name',
      width: 200,
      format: 'Text',
      alignment: 'Left',
      bold: false,
      italic: false,
      total: false
    },
    {
      id: 3,
      columnName: 'Số lượng',
      englishName: 'Quantity',
      width: 100,
      format: 'Number',
      alignment: 'Right',
      bold: false,
      italic: false,
      total: true
    },
    {
      id: 4,
      columnName: 'Đơn giá',
      englishName: 'Price',
      width: 120,
      format: 'Currency',
      alignment: 'Right',
      bold: false,
      italic: false,
      total: false
    },
    {
      id: 5,
      columnName: 'Thành tiền',
      englishName: 'Amount',
      width: 120,
      format: 'Currency',
      alignment: 'Right',
      bold: false,
      italic: false,
      total: true
    }
  ]);

  const handleCellValueChange = useCallback((rowId: number, field: keyof PrintColumnRow, value: unknown) => {
    setTableData(prevRows => prevRows.map(row => (row.id === rowId ? { ...row, [field]: value } : row)));
  }, []);

  const renderField = useCallback(
    (
      params: DataTableRenderCellParams<PrintColumnRow>,
      type: 'text' | 'number' | 'select' | 'checkbox',
      options?: BanHangRecord[]
    ) => (
      <CellFieldV2
        name={params.field}
        type={type}
        value={params.value}
        options={options}
        onValueChange={value => handleCellValueChange(params.row.id, params.field as keyof PrintColumnRow, value)}
        commitOnBlur
      />
    ),
    [handleCellValueChange]
  );

  const columns = useMemo<DataTableColumn[]>(
    () => [
      { field: 'columnName', headerName: 'Tên cột', width: 150, renderCell: params => renderField(params, 'text') },
      {
        field: 'englishName',
        headerName: 'Tên tiếng Anh',
        width: 150,
        renderCell: params => renderField(params, 'text')
      },
      {
        field: 'width',
        headerName: 'Độ rộng',
        width: 100,
        type: 'number',
        renderCell: params => renderField(params, 'number')
      },
      {
        field: 'format',
        headerName: 'Định dạng',
        width: 120,
        renderCell: params => renderField(params, 'select', formatOptions)
      },
      {
        field: 'alignment',
        headerName: 'Căn chỉnh',
        width: 100,
        renderCell: params => renderField(params, 'select', alignmentOptions)
      },
      { field: 'bold', headerName: 'In đậm', width: 80, renderCell: params => renderField(params, 'checkbox') },
      { field: 'italic', headerName: 'Nghiêng', width: 80, renderCell: params => renderField(params, 'checkbox') },
      { field: 'total', headerName: 'Tổng', width: 80, renderCell: params => renderField(params, 'checkbox') }
    ],
    [renderField]
  );

  const handleRowClick: DataTableRowEventHandler<PrintColumnRow> = params => setSelectedRowId(params.id);

  const actions = useMemo(
    () => ({
      add: () =>
        setTableData(prevRows => [
          ...prevRows,
          {
            id: Date.now(),
            columnName: '',
            englishName: '',
            width: 100,
            format: 'Text',
            alignment: 'Left',
            bold: false,
            italic: false,
            total: false
          }
        ]),
      delete: () => setTableData(prevRows => prevRows.filter(row => String(row.id) !== selectedRowId)),
      copy: () =>
        setTableData(prevRows => {
          const row = prevRows.find(item => String(item.id) === selectedRowId);
          return row ? [...prevRows, { ...row, id: Date.now() }] : prevRows;
        }),
      moveUp: () =>
        setTableData(prevRows => {
          const index = prevRows.findIndex(row => String(row.id) === selectedRowId);
          if (index <= 0) return prevRows;
          const nextRows = [...prevRows];
          [nextRows[index - 1], nextRows[index]] = [nextRows[index], nextRows[index - 1]];
          return nextRows;
        }),
      moveDown: () =>
        setTableData(prevRows => {
          const index = prevRows.findIndex(row => String(row.id) === selectedRowId);
          if (index < 0 || index >= prevRows.length - 1) return prevRows;
          const nextRows = [...prevRows];
          [nextRows[index], nextRows[index + 1]] = [nextRows[index + 1], nextRows[index]];
          return nextRows;
        }),
      pin: () => {},
      export: () => {},
      paste: () => {}
    }),
    [selectedRowId]
  );

  return (
    <div className='space-y-4'>
      <div className='h-[400px]'>
        <ErpInputTableV2
          rows={tableData}
          columns={columns}
          selectedRowId={selectedRowId}
          onRowClick={handleRowClick}
          getRowId={row => String(row.id)}
          actions={actions}
          actionOrder={['add', 'delete', 'copy', 'moveUp', 'moveDown', 'pin', 'export', 'paste']}
        />
      </div>
    </div>
  );
};

export default EditPrintTemplatePrintColumns;
